import { z } from 'zod';
import { ChartProvenanceSchema } from './provenance.js';

// ---- Artifact kinds ---------------------------------------------------------
//
// Discriminated union of renderable artifact payloads streamed from the agent
// via `AgentEventSchema` (`type: 'artifact'`). Each kind maps to a distinct
// card component in the analyst dashboard (#1869). The `id` field is stable
// within a conversation turn and can be used for keyed React reconciliation.

const KpiItemSchema = z
  .object({
    label: z.string(),
    value: z.number(),
    unit: z.string().optional(),
    delta: z.number().optional(),
  })
  .strict();

const KpiArtifactSchema = z
  .object({
    kind: z.literal('kpi'),
    id: z.string(),
    title: z.string(),
    items: z.array(KpiItemSchema),
  })
  .strict();

const SeriesPointSchema = z
  .object({
    x: z.string(),
    y: z.number(),
  })
  .strict();

// WHY (#2006, epic #2004): `points` is untrusted agent output; the .max(500)
//   clamp refuses an abusively large series while staying generous for real
//   charts (daily points across a long window stay well under the cap).
const SeriesSchema = z
  .object({
    name: z.string(),
    points: z.array(SeriesPointSchema).max(500),
  })
  .strict();

// WHY (#2006, epic #2004): v2 reuses this same chart schema (no forked v2
//   union). `stacked_bar` extends the renderable chart types; optional
//   `provenance`/`createdAt` let v2 attach an evidence trail and a timestamp
//   without breaking back-compat (v1 bar/line charts omitting them still
//   parse). The .max(20) series clamp bounds untrusted agent output.
// SECURITY (#2006, epic #2004): `provenance` is the SQL-free `ChartProvenanceSchema`
//   (not the full `ProvenanceSchema`) — chart cards are broadcast to all
//   `dashboard.read` chat users, so raw SQL must NOT be representable here.
const ChartArtifactSchema = z
  .object({
    kind: z.literal('chart'),
    id: z.string(),
    title: z.string(),
    description: z.string().optional(),
    chartType: z.enum(['bar', 'line', 'stacked_bar']),
    series: z.array(SeriesSchema).max(20),
    provenance: ChartProvenanceSchema.optional(),
    createdAt: z.string().datetime().optional(),
  })
  .strict();

const TableColumnSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    align: z.enum(['left', 'right']).optional(),
  })
  .strict();

// WHY: rows is z.record (open) — column keys are dynamic and unknown at schema
//   definition time. Applying .strict() to a record is impossible; the open
//   shape is intentional per spec.
// WHY (#2006, epic #2004): `rows`/`columns` are untrusted agent output, so the
//   .max() clamps refuse an abusively large table while staying generous for
//   real leadership tables. The row/cell VALUES stay open business data (BR-4);
//   forbidden-column/secret content is enforced upstream by the SENSITIVE
//   block-list (#2017), not by this presentation contract.
const TableArtifactSchema = z
  .object({
    kind: z.literal('table'),
    id: z.string(),
    title: z.string(),
    columns: z.array(TableColumnSchema).max(50),
    rows: z.array(z.record(z.union([z.string(), z.number()]))).max(1000),
  })
  .strict();

const TextArtifactSchema = z
  .object({
    kind: z.literal('text'),
    id: z.string(),
    title: z.string().optional(),
    body: z.string(),
  })
  .strict();

/**
 * @aiPurpose discriminated union of all renderable artifact payloads that the
 *   agent may emit during a streaming run. Consumers MUST switch on `kind` and
 *   handle every variant; unknown kinds are rejected at parse-time.
 * @aiInvariant `id` is stable for a given artifact within one conversation turn
 *   and may be used as a React key. New kinds require a coordinated shared +
 *   backend + web change (append-only discriminant set).
 */
export const ArtifactSchema = z.discriminatedUnion('kind', [
  KpiArtifactSchema,
  ChartArtifactSchema,
  TableArtifactSchema,
  TextArtifactSchema,
]);

export type Artifact = z.infer<typeof ArtifactSchema>;
